/**
 * Line Chart Type Definition
 */

import {
  paletteControl,
  gridControl,
  titleControl,
  xLabelControl,
  yLabelControl,
  xMinControl,
  xMaxControl,
  yMinControl,
  yMaxControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  xAxisRangeCode,
  yAxisRangeCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';
import { SERIES_LINE_STYLES_MPL, SERIES_MARKERS_MPL } from '../../utils/accessibility';
import { getColors } from '../shared/palettes';

const lineChart = {
  id: 'line-chart',
  name: 'Line Chart',
  category: 'line',
  description: 'Show trends and changes over time',
  
  sampleData: [
    // Series A
    { x: 0, y: 35, series: 'Series A' },
    { x: 10, y: 58, series: 'Series A' },
    { x: 20, y: 72, series: 'Series A' },
    { x: 30, y: 81, series: 'Series A' },
    { x: 40, y: 87, series: 'Series A' },
    { x: 50, y: 91, series: 'Series A' },
    { x: 60, y: 93, series: 'Series A' },
    { x: 70, y: 94, series: 'Series A' },
    // Series B
    { x: 0, y: 42, series: 'Series B' },
    { x: 10, y: 61, series: 'Series B' },
    { x: 20, y: 74, series: 'Series B' },
    { x: 30, y: 82, series: 'Series B' },
    { x: 40, y: 87, series: 'Series B' },
    { x: 50, y: 90, series: 'Series B' },
    { x: 60, y: 91.5, series: 'Series B' },
    { x: 70, y: 92, series: 'Series B' },
    // Series C
    { x: 0, y: 30, series: 'Series C' },
    { x: 10, y: 54, series: 'Series C' },
    { x: 20, y: 68, series: 'Series C' },
    { x: 30, y: 78, series: 'Series C' },
    { x: 40, y: 85, series: 'Series C' },
    { x: 50, y: 89, series: 'Series C' },
    { x: 60, y: 92, series: 'Series C' },
    { x: 70, y: 94, series: 'Series C' },
  ],
  
  defaultConfig: {
    externalColorMode: 'palette',
    useBluesPalette: true,
    useRedsPalette: false,
    useGreensPalette: false,
    showGrid: true,
    lineWidth: 2,
    showMarkers: true,
    markerSize: 6,
    useSeriesStyles: false,
    showLegend: true,
    legendPosition: 'top',
    xlabel: 'X',
    ylabel: 'Y',
    title: '',
    xMin: '',
    xMax: '',
    yMin: '',
    yMax: '',
    labelRotation: null,
    truncateXAxisLabels: false,
  },
  
  controls: [
    paletteControl,
    {
      key: 'lineWidth',
      label: 'Line Width',
      type: 'slider',
      min: 1,
      max: 5,
      step: 0.5,
      unit: 'px',
    },
    {
      key: 'showMarkers',
      label: 'Show Markers',
      type: 'toggle',
    },
    {
      key: 'markerSize',
      label: 'Marker Size',
      type: 'slider',
      min: 3,
      max: 12,
      step: 1,
      unit: 'px',
    },
    gridControl,
    {
      key: 'useSeriesStyles',
      label: 'Use Non-Color Series Styles',
      type: 'toggle',
    },
    {
      key: 'showLegend',
      label: 'Show Legend',
      type: 'toggle',
    },
    {
      key: 'legendPosition',
      label: 'Legend Position',
      type: 'select',
      options: [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Right', value: 'right' },
      ],
    },
    labelRotationControl,
    truncateXAxisLabelsControl,
    xLabelControl,
    yLabelControl,
    titleControl,
    xMinControl,
    xMaxControl,
    yMinControl,
    yMaxControl,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasWideUserData = dataContext?.isLoaded && dataContext.mapping?.x && dataContext.mapping?.inputShape === 'wide' && Array.isArray(dataContext.mapping?.yColumns) && dataContext.mapping.yColumns.length > 0;
    const hasLongUserData = dataContext?.isLoaded && dataContext.mapping?.x && dataContext.mapping?.y;
    const hasUserData = hasWideUserData || hasLongUserData;
    const colors = JSON.stringify(getColors(config, hasWideUserData ? dataContext.mapping.yColumns.length : 5, s));

    const lineStyles = JSON.stringify(SERIES_LINE_STYLES_MPL);
    const markerStyles = JSON.stringify(SERIES_MARKERS_MPL);
    const useSeriesStyles = !!config.useSeriesStyles;

    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? (hasWideUserData
  ? `df = pd.read_csv('${dataContext.fileName}')
epochs = df['${dataContext.mapping.x}'].tolist()
series_columns = ${JSON.stringify(dataContext.mapping.yColumns)}
series_data = {column_name: df[column_name].tolist() for column_name in series_columns}`
  : dataContext.mapping.series
  ? `df = pd.read_csv('${dataContext.fileName}')
# Build series dict from grouped data
series_data = {}
for name, group in df.groupby('${dataContext.mapping.series}'):
    group = group.sort_values('${dataContext.mapping.x}')
    series_data[str(name)] = group['${dataContext.mapping.y}'].tolist()
epochs = df.groupby('${dataContext.mapping.series}').apply(lambda g: g.sort_values('${dataContext.mapping.x}')['${dataContext.mapping.x}'].tolist()).iloc[0]`
  : `df = pd.read_csv('${dataContext.fileName}')
epochs = df['${dataContext.mapping.x}'].tolist()
series_data = {'Series 1': df['${dataContext.mapping.y}'].tolist()}`)
: `epochs = [0, 10, 20, 30, 40, 50, 60, 70]  # X-axis values

series_data = {
    'GPT Models': [35, 58, 72, 81, 87, 91, 93, 94],  # Series 1
    'BERT Models': [42, 61, 74, 82, 87, 90, 91.5, 92],  # Series 2
    'LLaMA Models': [30, 54, 68, 78, 85, 89, 92, 94]  # Series 3
}`}
# ======================
${createFigure(5.5, 4.5, 300, s)}

# Define colors for each series
colors = ${colors}
line_styles = ${lineStyles}
marker_styles = ${markerStyles}

# Plot each series
for idx, (series_name, values) in enumerate(series_data.items()):
    line_style = line_styles[idx % len(line_styles)] if ${useSeriesStyles ? 'True' : 'False'} else '-'
    marker_style = marker_styles[idx % len(marker_styles)] if ${useSeriesStyles ? 'True' : 'False'} else 'o'
    ax.plot(epochs, values,
           color=colors[idx % len(colors)],
           linewidth=${config.lineWidth || 2},
           linestyle=line_style,
           ${config.showMarkers ? `marker=marker_style, markersize=${config.markerSize || 6},` : ''}
           label=series_name,
           alpha=0.9)
${titleCode(config, s)}
${axisLabelsCode(config, s)}

# Add grid
${gridCode(config, 'both', s)}

${config.showLegend ? `# Add legend
ax.legend(loc='${config.legendPosition === 'top' ? 'upper center' : config.legendPosition === 'bottom' ? 'lower center' : 'center left'}',
         bbox_to_anchor=(${config.legendPosition === 'top' ? '0.5, 1.12' : config.legendPosition === 'bottom' ? '0.5, -0.15' : '1.05, 0.5'}),
         frameon=False, fontsize=${s?.legendSize || 10}${config.legendPosition === 'right' ? '' : ', ncol=3'})` : '# Legend disabled'}

# Set axis ranges
${xAxisRangeCode(config)}
${yAxisRangeCode(config)}
${spineCode(s)}
${finishCode('line_chart')}`;
  },
};

export default lineChart;
